import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Loader from '../components/Loader'
import { get_card_renew } from '../services/api'
import { ICardRenewData } from '../models/IApiResponses'
import { LOCALES } from '../locales/locales'
import { TLanguages } from '../models/ILocales'
import { isIOSDevice } from '../utils/browserDetection'

/**
 * Страница перевыпуска карты Apple Wallet.
 * Гость приходит по ссылке с обратной стороны старой карты: /renew/<token>.
 * Авторизации нет — гость определяется токеном из URL.
 * Оформление намеренно нейтральное, siteDesigns здесь не используется.
 */
const CardRenew: React.FC = () => {
    const { token } = useParams<{ token: string }>()
    const [currentLocale] = useState<TLanguages>(
      (localStorage.getItem('currentLocale') as TLanguages) || 'ru_RU'
    )
    const [isIOS] = useState<boolean>(isIOSDevice())

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isLinkInvalid, setIsLinkInvalid] = useState<boolean>(false) // 404
    const [hasLoadError, setHasLoadError] = useState<boolean>(false) // сеть / 5xx
    const [data, setData] = useState<ICardRenewData | null>(null)

    const t = LOCALES[currentLocale]

    useEffect(() => {
        if (!token) {
            setIsLoading(false)
            setIsLinkInvalid(true)
            return
        }

        get_card_renew(token)
          .then(res => {
              setData(res.data)
              setIsLoading(false)
          })
          .catch((err: { response?: { status?: number } }) => {
              setIsLoading(false)
              if (err?.response?.status === 404) {
                  setIsLinkInvalid(true)
              } else {
                  setHasLoadError(true)
              }
          })
    }, [token])

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-6 text-center">

              {isLoading && (
                <div className="flex justify-center py-8">
                    <Loader color={'#000000'} />
                </div>
              )}

              {!isLoading && isLinkInvalid && (
                <>
                    <h1 className="text-xl font-semibold text-gray-900 mb-4">
                        {t.RENEW_TITLE}
                    </h1>
                    <p className="text-base text-gray-700">{t.RENEW_INVALID_LINK}</p>
                </>
              )}

              {!isLoading && hasLoadError && (
                <>
                    <h1 className="text-xl font-semibold text-gray-900 mb-4">
                        {t.RENEW_TITLE}
                    </h1>
                    <p className="text-base text-gray-700">{t.RENEW_LOAD_ERROR}</p>
                </>
              )}

              {!isLoading && data && (
                <>
                    <h1 className="text-xl font-semibold text-gray-900 mb-4">
                        {data.org}
                    </h1>

                    {!!data.customer_name && (
                      <p className="text-base text-gray-900 mb-2">
                          {t.RENEW_HELLO}, {data.customer_name}!
                      </p>
                    )}

                    {!!data.card_number && (
                      <p className="text-sm text-gray-500 mb-4">
                          {t.RENEW_CARD_NUMBER} {data.card_number}
                      </p>
                    )}

                    <p className="text-base text-gray-700 mb-6">
                        {data.already_renewed ? t.RENEW_ALREADY_RENEWED : t.RENEW_DESCRIPTION}
                    </p>

                    {/* Обычная ссылка, без Blob: иначе iOS не предложит добавить карту */}
                    <a
                      href={data.download_url}
                      className="block w-full py-4 px-5 rounded-xl bg-black text-white text-lg font-semibold no-underline"
                    >
                        {t.RENEW_BUTTON}
                    </a>

                    {!isIOS && (
                      <p className="text-sm text-gray-500 mt-4">{t.RENEW_IOS_ONLY}</p>
                    )}
                </>
              )}

          </div>
      </div>
    )
}

export default CardRenew
